/**
 * Compute Cash Available for New Trades.
 * Formula: Non-Margin Buying Power (conservative cash available)
 * 
 * This is the conservative amount available for new trades.
 * Does NOT include Margin Buying Power or double-count Settled Cash.
 * 
 * Returns the result rounded to exactly 2 decimal places.
 * Treats NaN/undefined/null inputs as 0.
 */
export function computeCashAvailable(
  settledAvailableCash: number,
  nonMarginBuyingPower: number,
  marginBuyingPower: number,
  cashReservedForOptions: number,
  targetCashReserve: number
): number {
  const nonMargin = Number(nonMarginBuyingPower) || 0;
  // `+ 0` normalizes a rounded -0 (from a tiny negative input) to +0 — avoids a
  // meaningless "-$0.00" and -0/+0 comparison quirks downstream.
  return Math.round(nonMargin * 100) / 100 + 0;
}

/**
 * Compute Excess Cash After Target Reserve.
 * Formula: max(0, Non-Margin Buying Power - Target Cash Reserve)
 */
export function computeExcessCashAfterReserve(
  nonMarginBuyingPower: number,
  targetCashReserve: number
): number {
  const nonMargin = Number(nonMarginBuyingPower) || 0;
  const target = Number(targetCashReserve) || 0;
  return Math.round(Math.max(0, nonMargin - target) * 100) / 100;
}

/**
 * Compute Total Liquidity View.
 * Formula: Non-Margin Buying Power + Margin Buying Power
 */
export function computeTotalLiquidity(
  nonMarginBuyingPower: number,
  marginBuyingPower: number
): number {
  const nonMargin = Number(nonMarginBuyingPower) || 0;
  const margin = Number(marginBuyingPower) || 0;
  return Math.round((nonMargin + margin) * 100) / 100;
}
